import { useState } from "react";
import { Box, Flex, Text, Image, Icon, Heading } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import FindStylists from "../assets/images/flexibility.png";
import Ratings from "../assets/images/confidence.png";
import Services from "../assets/images/financial.png";

const MotionBox = motion(Box);

const cardData = [
  {
    id: 1,
    title: "Total Booking Flexibility",
    description:
      "Book anytime, anywhere with 24/7 access to stylists near you. Whether you prefer the traditional salon experience or the convenience of a skilled professional coming to your home, you can easily schedule and manage one-off or recurring appointments on your own terms.",
    image: FindStylists,
    bg: "#D9ED8C",
  },
  {
    id: 2,
    title: "More Confidence & Communication",
    description:
      "Make informed decisions by browsing professional portfolios and reading authentic reviews from other clients. Use our secure in-app messenger to share inspiration photos and confirm your desired look directly with your stylist before your appointment.",
    image: Ratings,
    bg: "#8FD3D6",
  },
  {
    id: 3,
    title: "Financial Security",
    description:
      "Enjoy total peace of mind with our transparent pricing guarantee. View clear, upfront costs for all services and stylists before you book, eliminating unexpected fees and ensuring you know exactly what you're paying for every time.",
    image: Services,
    bg: "#FF8E3C",
  },
];

const PulseIndicator = () => (
  <Box position="relative" w="16px" h="16px">
    <MotionBox
      position="absolute"
      top="0"
      left="0"
      w="16px"
      h="16px"
      borderRadius="full"
      bg="rgba(255,255,255,0.4)"
      animate={{
        scale: [1, 2.8, 1],
        opacity: [0.6, 0, 0.6],
      }}
      // @ts-ignore
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
    <MotionBox
      position="absolute"
      top="0"
      left="0"
      w="16px"
      h="16px"
      borderRadius="full"
      bg="rgba(255,255,255,0.35)"
      animate={{
        scale: [1, 2, 1],
        opacity: [0.8, 0, 0.8],
      }}
      // @ts-ignore
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 0.3,
      }}
    />
    <Box
      position="absolute"
      top="0"
      left="0"
      w="16px"
      h="16px"
      borderRadius="full"
      bg="white"
      boxShadow="0 0 8px rgba(0,0,0,0.18)"
      borderWidth="1.5px"
      borderColor="rgba(255,255,255,0.9)"
    />
  </Box>
);

const DesktopFlipCard = ({ card }: { card: (typeof cardData)[0] }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <Box
      style={{ perspective: "1200px" }}
      h="740px"
      cursor="pointer"
      position="relative"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
    >
      {/* Pulsating indicator */}
      {!isFlipped && (
        <Box position="absolute" top="22px" right="22px" zIndex={10}>
          <PulseIndicator />
        </Box>
      )}

      <motion.div
        style={{
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          position: "relative",
        }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Front */}
        <Box
          position="absolute"
          w="100%"
          h="100%"
          style={{ backfaceVisibility: "hidden" }}
          bg={card.bg}
          borderRadius="24px"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Text
            fontSize="28px"
            fontWeight={600}
            color="#19202D"
            fontFamily="LeagueSpartan"
            px="28px"
            pt="28px"
            pb="16px"
            textAlign="center"
          >
            {card.title}
          </Text>
          <Box
            flex={1}
            px="16px"
            pb="16px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Image
              src={card.image}
              w="100%"
              h="100%"
              objectFit="contain"
              borderRadius="16px"
            />
          </Box>
        </Box>

        {/* Back */}
        <Box
          position="absolute"
          w="100%"
          h="100%"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
          bg={card.bg}
          borderRadius="24px"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          p="40px"
        >
          <Text
            fontSize="28px"
            fontWeight={600}
            color="#19202D"
            fontFamily="LeagueSpartan"
            mb="20px"
            textAlign="center"
          >
            {card.title}
          </Text>
          <Text
            fontSize="16px"
            color="#19202D"
            fontFamily="LeagueSpartan"
            lineHeight="170%"
            textAlign="center"
            fontWeight={400}
          >
            {card.description}
          </Text>
        </Box>
      </motion.div>
    </Box>
  );
};

const MobileFlipCard = ({ card }: { card: (typeof cardData)[0] }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <Box
      style={{ perspective: "1200px" }}
      h="420px"
      cursor="pointer"
      onClick={() => setIsFlipped(!isFlipped)}
      position="relative"
    >
      {/* Pulsating indicator */}
      {!isFlipped && (
        <Box position="absolute" top="20px" right="20px" zIndex={10}>
          <PulseIndicator />
        </Box>
      )}

      <motion.div
        style={{
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          position: "relative",
        }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Front */}
        <Box
          position="absolute"
          w="100%"
          h="100%"
          style={{ backfaceVisibility: "hidden" }}
          bg={card.bg}
          borderRadius="24px"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Text
            fontSize="24px"
            fontWeight={600}
            color="#19202D"
            fontFamily="LeagueSpartan"
            px="24px"
            pt="24px"
            pb="12px"
            textAlign="center"
          >
            {card.title}
          </Text>
          <Box
            flex={1}
            px="14px"
            pb="14px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Image
              src={card.image}
              w="100%"
              h="100%"
              objectFit="contain"
              borderRadius="14px"
            />
          </Box>
        </Box>

        {/* Back */}
        <Box
          position="absolute"
          w="100%"
          h="100%"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
          bg={card.bg}
          borderRadius="24px"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          p="28px"
        >
          <Text
            fontSize="24px"
            fontWeight={600}
            color="#19202D"
            fontFamily="LeagueSpartan"
            mb="16px"
            textAlign="center"
          >
            {card.title}
          </Text>
          <Text
            fontSize="15px"
            color="#19202D"
            fontFamily="LeagueSpartan"
            lineHeight="170%"
            textAlign="center"
            fontWeight={400}
          >
            {card.description}
          </Text>
        </Box>
      </motion.div>
    </Box>
  );
};

export const Features = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const prev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const next = () =>
    setCurrentIndex((i) => Math.min(cardData.length - 1, i + 1));

  return (
    <Box
      py={{ base: "60px", lg: "132px" }}
      px={{ base: "16px", md: "40px", lg: "163px" }}
      pos="relative"
      bg="#FFF"
      maxW={{ base: "100%", "2xl": "1700px" }}
      mx="auto"
    >
      {/* Decorative glow */}
      <Box
        position="absolute"
        top={0}
        left={-500}
        width={{ base: "80%", lg: "50%" }}
        height={{ base: "40%", lg: "50%" }}
        zIndex={1}
        opacity={0.6}
        bg="radial-gradient(circle, #8FD3D6 0%, #EEF4F200 70%)"
        filter="blur(144px)"
      />

      <Heading
        fontSize={{ base: "28px", lg: "52px" }}
        color={"#19202D"}
        lineHeight={"100%"}
        fontFamily={"LeagueSpartan"}
        textAlign={"center"}
        mb="44px"
      >
        Why Book-d?
      </Heading>

      {/* Desktop: 3-column grid */}
      <Box
        display={{ base: "none", lg: "grid" }}
        gridTemplateColumns="repeat(3, 1fr)"
        gap="30px"
        zIndex={99}
        position="relative"
      >
        {cardData.map((card) => (
          <DesktopFlipCard key={card.id} card={card} />
        ))}
      </Box>

      {/* Mobile: Carousel */}
      <Box
        display={{ base: "block", lg: "none" }}
        position="relative"
        zIndex={99}
      >
        <Flex align="center" gap="6px">
          {/* Left arrow */}
          <Flex
            w="36px"
            h="36px"
            borderRadius="full"
            align="center"
            justify="center"
            bg={currentIndex === 0 ? "transparent" : "rgba(25, 32, 45, 0.06)"}
            cursor={currentIndex === 0 ? "default" : "pointer"}
            onClick={prev}
            opacity={currentIndex === 0 ? 0.25 : 1}
            flexShrink={0}
            transition="all 0.2s"
          >
            <Icon size="lg" color="#19202D">
              <FiChevronLeft />
            </Icon>
          </Flex>

          {/* Card container */}
          <Box flex={1} overflow="hidden" borderRadius="24px">
            <motion.div
              style={{ display: "flex" }}
              animate={{ x: `-${currentIndex * 100}%` }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            >
              {cardData.map((card) => (
                <Box key={card.id} minW="100%" px="2px">
                  <MobileFlipCard card={card} />
                </Box>
              ))}
            </motion.div>
          </Box>

          {/* Right arrow */}
          <Flex
            w="36px"
            h="36px"
            borderRadius="full"
            align="center"
            justify="center"
            bg={
              currentIndex === cardData.length - 1
                ? "transparent"
                : "rgba(25, 32, 45, 0.06)"
            }
            cursor={
              currentIndex === cardData.length - 1 ? "default" : "pointer"
            }
            onClick={next}
            opacity={currentIndex === cardData.length - 1 ? 0.25 : 1}
            flexShrink={0}
            transition="all 0.2s"
          >
            <Icon size="lg" color="#19202D">
              <FiChevronRight />
            </Icon>
          </Flex>
        </Flex>

        {/* Dot indicators */}
        <Flex justify="center" gap="8px" mt="20px">
          {cardData.map((_, idx) => (
            <Box
              key={idx}
              w={currentIndex === idx ? "24px" : "8px"}
              h="8px"
              borderRadius="full"
              bg={currentIndex === idx ? "#19202D" : "rgba(25, 32, 45, 0.15)"}
              transition="all 0.3s"
              cursor="pointer"
              onClick={() => setCurrentIndex(idx)}
            />
          ))}
        </Flex>
      </Box>

      {/* Decorative glow */}
      <Box
        position="absolute"
        bottom={0}
        right={0}
        width={{ base: "35%", lg: "15%" }}
        height={{ base: "35%", lg: "50%" }}
        zIndex={1}
        opacity={0.6}
        bg="radial-gradient(circle, #FF8E3C 0%, #EEF4F200 70%)"
        filter="blur(144px)"
      />
    </Box>
  );
};
